import React, { useState } from 'react';
import { slice, uniqBy } from 'lodash';
import {
  Avatar, IconButton, ListItemText, Popover, Tooltip,
} from '@mui/material';
import { Close, DoNotDisturb, Info } from '@mui/icons-material';
import { Credit } from 'lib/types/credits';
import { getAvatarUrlForCredit } from 'lib/utils/imageUtils';
import { CreditItem } from 'components/CreditItem';
import { AddCreditAvatar } from 'components/AddCreditAvatar';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { MobileTooltip } from 'components/MobileTooltip';
import { useRouter } from 'next/router';

const AVATAR_SIZE = 30;

interface Props {
  postId: number;
  credits: Credit[];
  numCreditsToShow?: number;
  path?: string;
  onIgnoreSuggestedCredit?: (postId: number) => void;
  isProfileOwner?: boolean;
  disableLinks?: boolean;
  newPostCard?: boolean;
}

export const CreditsPreview = ({
  postId,
  credits,
  numCreditsToShow = 5,
  path = '/',
  onIgnoreSuggestedCredit,
  isProfileOwner,
  disableLinks = false,
  newPostCard = false,
}: Props) => {
  const API = useApi();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const currentUser = useCurrentUser();
  const router = useRouter();

  const toggle = (event: React.MouseEvent<HTMLElement>) => {
    if (!anchorEl && credits?.length > 0) {
      setAnchorEl(event.currentTarget);
      return;
    }
    setAnchorEl(null);
  };

  const close = () => {
    setAnchorEl(null);
  };

  const onClickIgnoreSuggestedCredit = async () => {
    try {
      await API.ignoreSuggestedCredit(postId);
      if (onIgnoreSuggestedCredit) {
        onIgnoreSuggestedCredit(postId);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const renderIgnoreSuggestedCredits = () => {
    if (router?.query?.creditedStatus !== 'suggested') {
      return null;
    }
    return (
      <MobileTooltip title="Ignore suggested credits on this post">
        <button type="button" onClick={onClickIgnoreSuggestedCredit} className="right-3 absolute">
          <DoNotDisturb className="text-3xl hover:text-orange" />
        </button>
      </MobileTooltip>
    );
  };

  const renderAddCreditBtn = (title: string) => {
    if (!currentUser?.id || disableLinks) {
      return null;
    }
    return <AddCreditAvatar title={title} path={path} newPostCard />;
  };

  const renderNoCreditsInfo = () => {
    if (credits?.length > 0 || path === '/explore') {
      return null;
    }
    const text = isProfileOwner ? 'This post is not yet credited and will not appear on your profile by default. Once you add a credit it will be visible to everyone on your profile.'
      : 'This post has no credits, but is owned by this account.';
    return (
      <MobileTooltip title={text}>
        <div className="ml-auto">
          <Info className="text-2xl" />
        </div>
      </MobileTooltip>
    );
  };

  const renderCreditAvatars = () => {
    if (credits?.length === 0) {
      return renderAddCreditBtn('This post has no credits yet, add some now!');
    }
    const deDupedCreditsByUser = uniqBy(credits, 'userId');
    const creditsDisplayed = (deDupedCreditsByUser
        && deDupedCreditsByUser.length > numCreditsToShow)
      ? slice(deDupedCreditsByUser, 0, numCreditsToShow) : deDupedCreditsByUser;
    return (
      <>
        {creditsDisplayed.map((credit) => {
          const creditUserName = credit.isPrivate ? 'Private credit' : credit?.user?.name;
          if (credit.isHidden) {
            return null;
          }

          return (
            <Tooltip
              title={(
                <>
                  <div className="text-sm font-semibold">{creditUserName}</div>
                  {credit?.role?.name}
                </>
              )}
              key={credit?.id}
              arrow
            >
              <Avatar
                className={`mr-1 ${newPostCard && 'border-2 border-hintgray hover:border-orange'}`}
                sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
                key={credit.id}
                variant="circular"
                src={getAvatarUrlForCredit(
                  credit.user.id, credit.user.avatarUrl, credit.isPrivate,
                )}
                alt={creditUserName}
                imgProps={{ loading: 'lazy' }}
              />
            </Tooltip>
          );
        })}
        {deDupedCreditsByUser.length > numCreditsToShow ? (
          <Avatar sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }} className="bg-black text-white border-2 border-hintgray text-xs font-semibold">
            {`+${deDupedCreditsByUser.length - numCreditsToShow}`}
          </Avatar>
        ) : renderAddCreditBtn('Add a credit')}
      </>
    );
  };

  const additionalCreditsPrimary = () => {
    const text = 'more credit(s)';
    return (
      <p className="font-bold">
        +
        {credits.length - numCreditsToShow}
        {' '}
        {text}
      </p>
    );
  };

  const additionalCreditsSecondary = () => {
    const text = 'Including:';
    return (
      <div>
        {text}
        {' '}
        {credits.slice(numCreditsToShow).map(
          (c) => (
            c.user?.name
          ),
        ).join(', ')}
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        className={`flex w-full ${newPostCard ? 'px-2' : 'p-2 hover:bg-hintgray hover:dark:bg-darkgray'}`}
        onClick={toggle}
      >
        {renderCreditAvatars()}
        {renderIgnoreSuggestedCredits()}
        {renderNoCreditsInfo()}
      </button>

      <Popover
        id="credit-preview"
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        PaperProps={{
          sx: {
            pointerEvents: 'auto',
            width: 280,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className="p-4">
          <div className="absolute right-2 top-2">
            <IconButton
              aria-label="close"
              onClick={close}
            >
              <Close />
            </IconButton>
          </div>
          <div className="mb-2">
            {/* TODO: sort hidden credits to bottom */}
            {credits?.slice(0, numCreditsToShow).map(
              (credit) => (
                <CreditItem
                  key={credit.id}
                  credit={credit}
                  disabled={disableLinks}
                />
              ),
            )}
            {credits?.length > numCreditsToShow && (
              <ListItemText
                primary={additionalCreditsPrimary()}
                secondary={additionalCreditsSecondary()}
              />
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};
